/**
 * The projects rail.
 *
 * A folder list above the conversations, and the only place projects are
 * created or changed. Lifted out of `ChatView` because the chat screen is
 * already the longest component in the app and a folder list has nothing to do
 * with streaming.
 *
 * Two interaction decisions worth stating.
 *
 * Deleting asks twice, inline, and never through `window.confirm`. A browser
 * modal blocks every event in the tab until it is dismissed, which breaks
 * automation and reads as a system error rather than a question — so the
 * button becomes its own confirmation and reverts if you look away.
 *
 * Renaming happens in place. A dialog for one text field is a dialog too many,
 * and the inline field can be escaped out of without deciding anything.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Check,
  FileStack,
  FolderPlus,
  Inbox,
  Layers,
  Pencil,
  ScrollText,
  UserPlus,
  Trash2,
  X,
} from 'lucide-react';
import {
  AI_PROJECT_INSTRUCTIONS_MAX,
  type AiProjectMemberRole,
  type AiProjectMemberView,
} from '@cenox/shared';
import type { AiProjectView } from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Spinner, cn } from '../../components/ui';
import { ProjectFiles } from './files/ProjectFiles';

/**
 * Which threads the list is showing.
 *
 * Three states, and all three are needed: `all` is every thread, `unfiled` is
 * the ones in no project, and a uuid is one project. Collapsing `unfiled` into
 * `all` would leave no way to see what has not been filed.
 */
export type ProjectScope = { kind: 'all' } | { kind: 'unfiled' } | { kind: 'project'; id: string };

export function ProjectRail({
  scope,
  onScope,
  onError,
}: {
  scope: ProjectScope;
  onScope: (next: ProjectScope) => void;
  onError: (message: string) => void;
}): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const client = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sharingId, setSharingId] = useState<string | null>(null);
  /** The project whose documents are open below it. One at a time, like sharing. */
  const [filesId, setFilesId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const newField = useRef<HTMLInputElement | null>(null);

  const projects = useQuery({
    queryKey: ['ai-projects', business?.businessId, showArchived],
    queryFn: () =>
      api.get<{ data: AiProjectView[]; filesAvailable?: boolean }>(
        path(`/ai/projects${showArchived ? '?archived=true' : ''}`),
      ),
    enabled: Boolean(business),
  });
  const filesAvailable = projects.data?.filesAvailable === true;

  const rows = projects.data?.data ?? [];
  const live = rows.filter((p) => !p.archivedAt);
  const archived = rows.filter((p) => p.archivedAt);

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['ai-projects'] });
    // The counts on each project are a function of the thread list.
    void client.invalidateQueries({ queryKey: ['ai-conversations'] });
  };

  const fail = (caught: unknown, fallback: string) =>
    onError(caught instanceof ApiError ? caught.message : fallback);

  useEffect(() => {
    if (creating) newField.current?.focus();
  }, [creating]);

  const create = useMutation({
    mutationFn: (name: string) => api.post<AiProjectView>(path('/ai/projects'), { name }),
    onSuccess: (made) => {
      setCreating(false);
      setNewName('');
      refresh();
      onScope({ kind: 'project', id: made.id });
    },
    onError: (caught) => fail(caught, 'Could not create that project.'),
  });

  const rename = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.patch(path(`/ai/projects/${input.id}`), { name: input.name }),
    onSuccess: () => {
      setRenamingId(null);
      refresh();
    },
    onError: (caught) => fail(caught, 'Could not rename that project.'),
  });

  const setArchived = useMutation({
    mutationFn: (input: { id: string; archived: boolean }) =>
      api.patch(path(`/ai/projects/${input.id}`), { archived: input.archived }),
    onSuccess: (_r, input) => {
      // Looking at a project you just archived would show an empty list.
      if (input.archived && scope.kind === 'project' && scope.id === input.id) {
        onScope({ kind: 'all' });
      }
      refresh();
    },
    onError: (caught) => fail(caught, 'Could not change that project.'),
  });

  const saveInstructions = useMutation({
    mutationFn: (input: { id: string; instructions: string | null }) =>
      api.patch(path(`/ai/projects/${input.id}`), { instructions: input.instructions }),
    onSuccess: () => {
      setEditingId(null);
      refresh();
    },
    onError: (caught) => fail(caught, 'Could not save those instructions.'),
  });

  /*
    Who is in the project being shared, and who could be. Both only while the
    panel is open: a sidebar does not need the membership of every project to
    draw a list of names.
  */
  const membersOf = useQuery({
    queryKey: ['ai-project-members', sharingId],
    queryFn: () => api.get<{ data: AiProjectMemberView[] }>(path(`/ai/projects/${sharingId!}/members`)),
    enabled: Boolean(sharingId),
  });

  const workspace = useQuery({
    queryKey: ['members', business?.businessId],
    queryFn: () => api.get<{ data: Array<{ userId: string | null; fullName: string | null; email: string; status: string }> }>(path('/members')),
    enabled: Boolean(sharingId),
  });

  const refreshMembers = () => client.invalidateQueries({ queryKey: ['ai-project-members'] });

  const invite = useMutation({
    mutationFn: (input: { projectId: string; userId: string; role: AiProjectMemberRole }) =>
      api.post(path(`/ai/projects/${input.projectId}/members`), {
        userId: input.userId,
        role: input.role,
      }),
    onSuccess: refreshMembers,
    onError: (caught) => fail(caught, 'Could not share the project with that person.'),
  });

  const setRole = useMutation({
    mutationFn: (input: { projectId: string; userId: string; role: AiProjectMemberRole }) =>
      api.patch(path(`/ai/projects/${input.projectId}/members/${input.userId}`), { role: input.role }),
    onSuccess: refreshMembers,
    onError: (caught) => fail(caught, 'Could not change that role.'),
  });

  const revoke = useMutation({
    mutationFn: (input: { projectId: string; userId: string }) =>
      api.del(path(`/ai/projects/${input.projectId}/members/${input.userId}`)),
    onSuccess: refreshMembers,
    onError: (caught) => fail(caught, 'Could not remove that person.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(path(`/ai/projects/${id}`)),
    onSuccess: (_r, id) => {
      setConfirmingId(null);
      if (scope.kind === 'project' && scope.id === id) onScope({ kind: 'all' });
      refresh();
    },
    onError: (caught) => fail(caught, 'Could not delete that project.'),
  });

  const selected = (p: AiProjectView) => scope.kind === 'project' && scope.id === p.id;

  return (
    <nav aria-label="Projects" className="border-b border-slate-200">
      <div className="flex items-center justify-between px-3 pt-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Projects</h2>
        <button
          type="button"
          onClick={() => setCreating((open) => !open)}
          aria-label="New project"
          title="New project"
          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <FolderPlus className="size-4" aria-hidden />
        </button>
      </div>

      {creating && (
        <form
          className="px-3 pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            const name = newName.trim();
            if (name) create.mutate(name);
          }}
        >
          <input
            ref={newField}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setCreating(false);
                setNewName('');
              }
            }}
            maxLength={120}
            placeholder="Project name"
            aria-label="Project name"
            className="h-8 w-full rounded-lg border border-slate-200 px-2 text-sm outline-none focus:border-brand-400"
          />
        </form>
      )}

      <ul className="space-y-0.5 p-2">
        <li>
          <Scope
            icon={<Layers className="size-4" aria-hidden />}
            label="All chats"
            active={scope.kind === 'all'}
            onClick={() => onScope({ kind: 'all' })}
          />
        </li>
        <li>
          <Scope
            icon={<Inbox className="size-4" aria-hidden />}
            label="No project"
            active={scope.kind === 'unfiled'}
            onClick={() => onScope({ kind: 'unfiled' })}
          />
        </li>

        {projects.isPending && (
          <li className="px-2 py-2">
            <Spinner label="Loading projects" />
          </li>
        )}

        {[...live, ...(showArchived ? archived : [])].map((p) => (
          <li key={p.id}>
            {renamingId === p.id ? (
              <form
                className="px-1 py-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = renameTo.trim();
                  if (name) rename.mutate({ id: p.id, name });
                }}
              >
                <input
                  autoFocus
                  value={renameTo}
                  onChange={(e) => setRenameTo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  maxLength={120}
                  aria-label={`Rename ${p.name}`}
                  className="h-8 w-full rounded-lg border border-brand-300 px-2 text-sm outline-none"
                />
              </form>
            ) : (
              <div
                className={cn(
                  'group flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors',
                  selected(p) ? 'bg-brand-50' : 'hover:bg-slate-50',
                  p.archivedAt && 'opacity-60',
                )}
              >
                <button
                  type="button"
                  onClick={() => onScope({ kind: 'project', id: p.id })}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{p.name}</span>
                  {/*
                    Visible without hovering, because whether instructions are
                    active changes every answer in the project — it is state a
                    person needs to see, not a control they need to find.
                  */}
                  {p.instructions ? (
                    <span
                      title="Instructions active"
                      aria-label={`${p.name} has instructions`}
                      className="shrink-0 rounded bg-brand-100 px-1 text-[10px] font-medium uppercase tracking-wide text-brand-700"
                    >
                      Instr
                    </span>
                  ) : null}
                  <span className="shrink-0 text-xs tabular-nums text-slate-400">
                    {p.conversationCount}
                  </span>
                </button>

                {/*
                  Files are for everybody the project is shared with: a viewer
                  reads them, an owner or editor also adds and removes them.
                  The panel decides which; the server agrees.
                */}
                {/*
                  Not offered at all until the deployment opens uploads
                  (AI_FILE_UPLOADS): a Files panel that can hold nothing would
                  only advertise something nobody may use yet.
                */}
                {filesAvailable && !p.archivedAt && confirmingId !== p.id && (
                  <span
                    className={cn(
                      'flex shrink-0 items-center transition-opacity group-hover:opacity-100 focus-within:opacity-100',
                      filesId === p.id ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    <Action
                      label={`Files in ${p.name}`}
                      onClick={() => setFilesId(filesId === p.id ? null : p.id)}
                    >
                      <FileStack className="size-3.5" aria-hidden />
                    </Action>
                  </span>
                )}

                {/* Only an owner may change a project. The server agrees. */}
                {p.mine && !p.archivedAt && confirmingId !== p.id && (
                  <span className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Action
                      label={`Rename ${p.name}`}
                      onClick={() => {
                        setRenamingId(p.id);
                        setRenameTo(p.name);
                      }}
                    >
                      <Pencil className="size-3.5" aria-hidden />
                    </Action>
                    <Action
                      label={`Share ${p.name}`}
                      onClick={() => setSharingId(sharingId === p.id ? null : p.id)}
                    >
                      <UserPlus className="size-3.5" aria-hidden />
                    </Action>
                    <Action
                      label={`${p.instructions ? 'Edit' : 'Add'} instructions for ${p.name}`}
                      onClick={() => {
                        setEditingId(editingId === p.id ? null : p.id);
                        setDraft(p.instructions ?? '');
                      }}
                    >
                      <ScrollText className="size-3.5" aria-hidden />
                    </Action>
                    <Action
                      label={`Archive ${p.name}`}
                      onClick={() => setArchived.mutate({ id: p.id, archived: true })}
                    >
                      <Archive className="size-3.5" aria-hidden />
                    </Action>
                    <Action label={`Delete ${p.name}`} onClick={() => setConfirmingId(p.id)}>
                      <Trash2 className="size-3.5" aria-hidden />
                    </Action>
                  </span>
                )}

                {p.mine && p.archivedAt && (
                  <Action
                    label={`Restore ${p.name}`}
                    onClick={() => setArchived.mutate({ id: p.id, archived: false })}
                  >
                    <ArchiveRestore className="size-3.5" aria-hidden />
                  </Action>
                )}

                {/*
                  The second ask. Inline rather than a browser dialog, and it
                  says what survives — the threads are the part people are
                  frightened of losing, and they do not go.
                */}
                {confirmingId === p.id && (
                  <span className="flex shrink-0 items-center gap-1">
                    <span className="text-xs text-slate-500">Delete? Chats stay.</span>
                    <Action label={`Confirm delete ${p.name}`} onClick={() => remove.mutate(p.id)}>
                      <Check className="size-3.5 text-red-600" aria-hidden />
                    </Action>
                    <Action label="Cancel delete" onClick={() => setConfirmingId(null)}>
                      <X className="size-3.5" aria-hidden />
                    </Action>
                  </span>
                )}
              </div>
            )}

            {/*
              Who the project is shared with.

              Inline under its project, like the instructions editor, and open
              one at a time. The sentence under the heading is not decoration:
              people reasonably assume sharing a folder shares what is in it,
              and it does not — so the screen says what sharing actually does
              before anybody uses it.
            */}
            {sharingId === p.id && (
              <div className="px-2 pb-3">
                <p className="text-[11px] leading-snug text-slate-500">
                  Members see the project and its instructions. They see a chat only when its
                  author shares that chat with the project — new chats stay private.
                </p>

                {membersOf.isPending ? (
                  <div className="pt-2">
                    <Spinner label="Loading members" />
                  </div>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {(membersOf.data?.data ?? []).map((m) => (
                      <li key={m.userId} className="flex items-center gap-1">
                        <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                          {m.fullName ?? m.email ?? m.userId}
                          {/*
                            The email beside the name here too: this list is
                            where somebody checks who they have given access
                            to, and two members can share a name.
                          */}
                          {m.fullName && m.email ? (
                            <span className="block truncate text-[10px] text-slate-400">{m.email}</span>
                          ) : null}
                        </span>
                        {m.role === 'owner' ? (
                          <span className="shrink-0 text-[11px] text-slate-400">Owner</span>
                        ) : (
                          <>
                            <select
                              aria-label={`Role for ${m.fullName ?? m.email ?? m.userId}`}
                              value={m.role}
                              onChange={(e) =>
                                setRole.mutate({
                                  projectId: p.id,
                                  userId: m.userId,
                                  role: e.target.value as AiProjectMemberRole,
                                })
                              }
                              className="h-7 shrink-0 rounded border border-slate-200 bg-white px-1 text-[11px] text-slate-700 outline-none focus:border-brand-400"
                            >
                              <option value="viewer">Viewer</option>
                              <option value="editor">Editor</option>
                            </select>
                            <Action
                              label={`Remove ${m.fullName ?? m.email ?? m.userId}`}
                              onClick={() => revoke.mutate({ projectId: p.id, userId: m.userId })}
                            >
                              <X className="size-3.5" aria-hidden />
                            </Action>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {/*
                  A list of people who are already here, never an email field.
                  An email box would be a way into the workspace wearing a
                  project's clothes — the server refuses it, and offering it
                  would still be the wrong promise.
                */}
                <select
                  aria-label={`Share ${p.name} with a colleague`}
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      invite.mutate({ projectId: p.id, userId: e.target.value, role: 'viewer' });
                    }
                  }}
                  className="mt-2 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus:border-brand-400"
                >
                  <option value="">Add someone from this workspace…</option>
                  {(workspace.data?.data ?? [])
                    .filter((m) => m.userId && m.status === 'active')
                    .filter((m) => !(membersOf.data?.data ?? []).some((x) => x.userId === m.userId))
                    .map((m) => (
                      <option key={m.userId!} value={m.userId!}>
                        {/*
                          Name AND email, always, not the name with the email
                          as a fallback. Two people in one workspace can share
                          a display name — CenoHub has two members called
                          "Onuzulike Chukwuemeka" — and a picker that shows the
                          name alone asks somebody to grant access to whichever
                          of them the list happened to put first.
                        */}
                        {m.fullName ? `${m.fullName} — ${m.email}` : m.email}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {filesAvailable && filesId === p.id && !p.archivedAt && <ProjectFiles project={p} />}

            {/*
              The editor, inline under its project. A textarea in a popover
              would be a popover containing the thing that shapes every answer
              in the project — it belongs where the project is.
            */}
            {editingId === p.id && (
              <form
                className="px-2 pb-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const next = draft.trim();
                  saveInstructions.mutate({ id: p.id, instructions: next ? next : null });
                }}
              >
                <label className="block text-[11px] text-slate-500" htmlFor={`instr-${p.id}`}>
                  Applies to every chat in this project
                </label>
                <textarea
                  id={`instr-${p.id}`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={AI_PROJECT_INSTRUCTIONS_MAX}
                  rows={4}
                  aria-label={`Instructions for ${p.name}`}
                  placeholder="e.g. Answer in bullet points and always state the date range."
                  className="mt-1 w-full resize-y rounded-lg border border-slate-200 p-2 text-xs outline-none focus:border-brand-400"
                />
                <div className="mt-1 flex items-center justify-between">
                  {/*
                    The cap is shown, not just enforced: instructions are
                    re-sent and re-billed on every question in the project, so
                    the length is a cost the owner is choosing.
                  */}
                  <span className="text-[11px] text-slate-400">
                    {draft.length}/{AI_PROJECT_INSTRUCTIONS_MAX} · sent with every question
                  </span>
                  <span className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saveInstructions.isPending}
                      className="rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-60"
                    >
                      Save
                    </button>
                  </span>
                </div>
              </form>
            )}
          </li>
        ))}

        {/*
          Always offered, never conditional on there being archived projects to
          see. The conditional version hid itself exactly when it was needed:
          archiving refetches the list WITHOUT archived rows, so the toggle
          vanished the moment somebody used it and the project looked deleted.
        */}
        {(
          <li className="px-2 pt-1">
            <button
              type="button"
              onClick={() => setShowArchived((on) => !on)}
              className="text-xs text-slate-500 underline-offset-2 hover:underline"
            >
              {showArchived ? 'Hide archived' : 'Show archived'}
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
}

function Scope({
  icon,
  label,
  active,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
        active ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-50',
      )}
    >
      <span className="text-slate-400">{icon}</span>
      {label}
    </button>
  );
}

/** A small icon button. Labelled, because an icon alone is not a name. */
function Action({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
    >
      {children}
    </button>
  );
}
